import { mdiChevronDown, mdiChevronLeft, mdiChevronRight, mdiChevronUp, mdiChartTree, mdiStar, mdiStarOutline, mdiDownload } from '@mdi/js';
import Icon from '@mdi/react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { getTextLang, LanguageContext } from '../components/providers/LanguageProvider';
import { Button, Card, Container, ContainerFlex, TextLocalized } from '../components/styled/Styled';
import { CategoryIndexAction, CategoryIndexContext } from '../components/providers/CategoryIndexProvider';
import { HttpConnection, HttpOperations, HttpMethod } from '../components/util/HttpConnection';
import { getBlockImage } from '../components/util/BlockUtil';
import { printComponent } from '../components/util/PrintUtil';
import ExcelJS from "exceljs";

const SingleAnswer = ({ item }) => {
    const { language } = useContext(LanguageContext);

    return <Container width="12" ph="8" pv="16" fontColor="color-black">
        {
            item.answers.map(answer => <ContainerFlex key={answer.id_answer} width="12" mv="16" align="center" multiline={false}>
                <input type="radio"
                    checked={answer?.user_answer != null || answer?.selected}
                    id={answer.id_answer}
                    disabled
                    value={answer?.id_answer ?? ""} name={item.id_answer} />
                <Container ml="8" fontWeight={answer?.user_answer != null || (answer?.selected ?? false) ? "bold" : "normal"} children={<label htmlFor={answer.id_answer}>{getTextLang(answer, 'description', language)}</label>} />
            </ContainerFlex>)
        }
    </Container>
}

const QuizItem = ({ item }) => {
    const { language } = useContext(LanguageContext);

    return <Card width="12" shadowOn mv="16" color={"color-white"}>
        <Card px="16" radiusbl="0px" radiusbr="0px" color="color-gray-card" width="12" fontSize="16px" fontWeight="300" fontColor="color-black"
            children={getTextLang(item, 'description', language)} />
        <Container width="12" height="1px" color="color-gray" />
        <SingleAnswer item={item} />
    </Card>
}

const CategoryGroup = ({ item }) => {
    const [group] = useState({ ...item });
    const resultRef = useRef();

    const languageText = useContext(LanguageContext);
    const { state, dispatch } = useContext(CategoryIndexContext);

    const actionNext = () => {
        dispatch({ action: CategoryIndexAction.next, data: item.id_group })
    }

    const actionToggle = () => {
        dispatch({ action: CategoryIndexAction.setIndex, data: item.id_group })
    }

    const printResult = (e) => {
        e.stopPropagation();
        printComponent(resultRef);
    }

    return <div ref={resultRef} className='col-12'>
        <Card shadowOn width="12" mt="16" color="color-white">
            <Button onClick={actionToggle} flex width="12" px="16" align="center" color={state.index === item.id_group ? "color-gray-card" : ""} radiusbl="0px" radiusbr="0px">
                <Container width="wrap" fontSize="16px" fontWeight="300" fontColor="color-black" children={getTextLang(group, 'name', languageText.language)} />

                {
                    state.index === item.id_group ?
                        <Button title="Download" flex onClick={printResult} align="center" fontColor="color-green-dark" color={"color-gray-card"} radiustl="0" radiusbl="0" children={<Icon size={1} path={mdiDownload} />} />
                        : null
                }
                {
                    <Button mh="16" ph="8"
                        fontColor="color-black"
                        children={<Icon path={group.info && group.info.important == true ? mdiStar : mdiStarOutline} size={1} />} />
                }
                <Container maxheight="24px" children={<Icon size={1} path={state.index === item.id_group ? mdiChevronUp : mdiChevronDown} />} />
            </Button>

            {
                state.index === item.id_group ? <Fragment>
                    <Container width="12" height="1px" color="color-gray" />
                    <ContainerFlex width="12" pv="16" ph="32" mb="32">
                        {item.questions.map((question) => <QuizItem key={question.id_question} item={question} />)}

                        <Card shadowOn width="12" mt="16" color={"color-white"}>
                            <Card px="16" radiusbl="0px" radiusbr="0px" color="color-gray-card" width="12" fontSize="16px" fontWeight="300" fontColor="color-black" children={<TextLocalized children="msgComment" />} />
                            <Container width="12" height="1px" color="color-gray" />
                            <ContainerFlex width="12" align="center">
                                <Container width="wrap" px="16" children={<input disabled value={group.info ? group.info.comment : ""} placeholder={languageText.dictionary["msgCommentHolder"]} type="text" />} />
                            </ContainerFlex>
                        </Card>
                        <ContainerFlex width="12" mt="32" justify="flex-end" fontColor="color-white">
                            <Button onClick={actionNext} pv="8" ph="16" color="color-green-dark" hoverColor="color-black" children={<TextLocalized children="actionNext" />} />
                        </ContainerFlex>
                    </ContainerFlex>

                </Fragment> : null
            }
        </Card>
    </div>
}

const PerformanceSectionView = () => {
    const [blocks, setBlocks] = useState([]);

    const [item, setItem] = useState();
    const [index, setIndex] = useState();

    const { dispatch } = useContext(CategoryIndexContext);
    const { language } = useContext(LanguageContext);

    let history = useNavigate();
    let location = useLocation();

    useEffect(() => {
        HttpConnection(HttpOperations.block, response => {
            //Set blocks
            setBlocks(response.item);
            setIndex(response.item.findIndex(block => block.id_block === location.state.item.id_block));
        }, error => console.log(error), HttpMethod.get);
    }, [location.state.item.id_block])

    useEffect(() => {
        if (index != null) {
            HttpConnection(`${HttpOperations.block}/${blocks[index].id_block}`,
                response => {
                    setItem(response.item);
                    //Set categories
                    dispatch({ action: CategoryIndexAction.setCategories, data: response.item.groups });
                }, error => console.log(error), HttpMethod.get);
        }
    }, [index, blocks, dispatch])

    const nextBlock = () => {
        setIndex(index + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const prevBlock = () => {
        setIndex(index - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const downloadInfo = () => {
        const workbook = new ExcelJS.Workbook();

        const getEstimatedRowHeight = (values, columnWidths) => {
            const baseLineHeight = 18;
            const lineCount = values.reduce((maxLines, value, index) => {
                const text = `${value ?? ''}`;
                const columnWidth = columnWidths[index] ?? 40;
                const estimatedCharsPerLine = Math.max(Math.floor(columnWidth * 1.15), 1);
                const wrappedLines = text
                    .split('\n')
                    .reduce((total, line) => total + Math.max(Math.ceil(line.length / estimatedCharsPerLine), 1), 0);

                return Math.max(maxLines, wrappedLines);
            }, 1);

            return Math.max(lineCount * baseLineHeight, 24);
        };

        item.groups.forEach(group => {
            const sheet = workbook.addWorksheet(getTextLang(group, 'name', language));
            sheet.columns = [
                { header: 'Question', key: 'question', width: 50 },
                { header: 'Answer', key: 'answer', width: 50 },
            ];

            const headerRow = sheet.getRow(1);
            headerRow.eachCell(cell => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF000000' }
                };
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
            headerRow.height = 24;

            group.questions.forEach(question => {
                const userAnswer = question.answers.find(answer => answer.user_answer != null);
                const rowData = {
                    question: getTextLang(question, 'description', language),
                    answer: userAnswer ? getTextLang(userAnswer, 'description', language) : "-"
                };
                const row = sheet.addRow(rowData);

                row.alignment = { vertical: 'center', horizontal: 'left', wrapText: true };
            });
        });

        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${item.id_block} - ${getTextLang(item, 'name', language)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    }


    return item ? (
        <ContainerFlex width="12" justify="center">
            <ContainerFlex width="12" lg="8" xl="8" justify="center" >
                <Card flex shadowOn color="color-white" width="12" mt="32" align="stretch">
                    <Card flex px="16" color="color-green-a40" justify="center" align="center" width="250px" radiusbr="0" radiustr="0" minheight="100%" >
                        <img alt={`block-${item.id_block}`} height="200px" width="100%" src={getBlockImage(item)} />
                    </Card>

                    <Container width="wrap" ph="8">
                        <Container width="12" ph="8" pv="8" fontColor="color-gray-dark" children={<TextLocalized children="msgBlockArea" />} />
                        <Container width="12" ph="8" fontSize="18px" fontWeight="bold" children={getTextLang(item, 'name', language)} />
                        <Container width="12" ph="8" pv="16" fontSize="14px" dangerouslySetInnerHTML={{ __html: getTextLang(item, 'description_long', language) }} />
                    </Container>

                    <Button title="Download" flex onClick={downloadInfo} px="8" align="center" fontColor="color-green-dark" color={"color-gray-card"} children={<Icon size={1} path={mdiDownload} />} />
                </Card>

                {item.groups.map((group) => <CategoryGroup key={group.id_group} item={group} />)}

                <ContainerFlex width="12" justify="space-around">
                    {
                        index > 0 ?
                            <ContainerFlex fontColor="color-black" align="center" pv="32">
                                <Button onClick={() => prevBlock()} flex justify="center" pr="16" align="center" fontWeight="bold" color="color-green-dark" hoverColor="color-black" fontColor="color-white">
                                    <Icon path={mdiChevronLeft} size={2} />
                                    <label children={getTextLang(blocks[index - 1], "name", language)} />
                                </Button>
                            </ContainerFlex>
                            : null
                    }
                    {
                        index < blocks.length - 1 ?
                            <ContainerFlex fontColor="color-black" align="center" pv="32">
                                <Button onClick={() => nextBlock()} flex justify="center" pl="16" align="center" fontWeight="bold" color="color-green-dark" hoverColor="color-black" fontColor="color-white">
                                    <label children={getTextLang(blocks[index + 1], "name", language)} />
                                    <Icon path={mdiChevronRight} size={2} />
                                </Button>
                            </ContainerFlex>
                            :
                            <ContainerFlex fontColor="color-black" align="center" pv="32">
                                <Button onClick={() => history("/performance")} flex justify="center" pl="16" align="center" fontWeight="bold" color="color-green-dark" hoverColor="color-black" fontColor="color-white">
                                    <label children={<TextLocalized children={"actionDoDiagnost"} />} />
                                    <ContainerFlex px="12" children={<Icon path={mdiChartTree} size={1} />} />
                                </Button>
                            </ContainerFlex>
                    }
                </ContainerFlex>
            </ContainerFlex>

        </ContainerFlex>
    ) : null;
};

export default PerformanceSectionView;